<template>
  <div id="app" :style="{ height: '100%' }" :data-theme="getWCAGSettings.contrastMode ? 'contrast' : 'default'">
    <mobile-suggester-screen />
    <connection-checker />
    <transition mode="in-out" enter-active-class="animated fadeIn faster" leave-active-class="animated fadeOut faster">
      <modal v-if="modalComponent" :component="modalComponent" :width="modalComponent.width" :key="modalKey"
        :class="{ modalBlur: showSuggester }"></modal>
    </transition>
    <transition appear mode="out-in" enter-active-class="animated fadeIn faster"
      leave-active-class="animated fadeOut faster" v-on:before-leave="beforePopperLeave"
      v-on:after-enter="afterPopperEnter">
      <popper v-if="popperComponent" :class="{
        modalBlur: needBlurBackground,
      }" :component="popperComponent" />
    </transition>
    <transition name="slide" enter-active-class="animated slideInDown faster"
      leave-active-class="animated slideOutUp faster">
      <dropdown-notification v-if="notification" v-bind="notification" />
    </transition>
    <template v-if="isLessonWithoutUser">
      <without-user-layout :needBlurBackground="needBlurBackground" />
    </template>
    <template v-else-if="!mainLayoutIsVisible">
      <redirect-layout :needBlurBackground="needBlurBackground" />
    </template>
    <template v-else>
      <main-layout :needBlurBackground="needBlurBackground" :needToHide="needToHideMainLayout" />
    </template>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import PushNotificationManager from '@/push_notification_manager';
import i18n from '@/lang/lang';
import WithoutUserLayout from '@/components/layouts/WithoutUserLayout';
import RedirectLayout from '@/components/layouts/RedirectLayout';
import MainLayout from '@/components/layouts/MainLayout';
import { fromShortToLong } from '@/components/shared/languageCodes';
// To prevent webpack import junk #number error
import DropdownNotification from '@/components/shared/elements/modal/DropdownNotification';
import cookieHandler from '@/cookie_handler';
import ModalMixin from '@/components/shared/elements/mixins/ModalMixin';

const Popper = () => import('@/components/shared/elements/modal/Popper');
const ConnectionChecker = () => import('@/components/shared/ConnectionChecker');
const Modal = () => import('@/components/shared/elements/modal/Modal');
const MobileSuggesterScreen = () =>
  import('@/components/shared/MobileSuggesterScreen');
export default {
  name: 'App',
  mixins: [ModalMixin],
  props: {},
  components: {
    MobileSuggesterScreen,
    DropdownNotification,
    ConnectionChecker,
    RedirectLayout,
    MainLayout,
    Popper,
    Modal,
    WithoutUserLayout,
  },
  data: () => ({
    modalLock: false,
    showPopover: false,
    isOpen: true,
    modalComponent: null,
    popperComponent: null,
    popperEntered: false,
    popoverComponent: null,
    showSuggester: false,
    modelOpened: false,
    notification: null,
    lastModalComponent: null,
  }),
  created() {
    document.addEventListener('visibilitychange', this.handleFocus);
    window.addEventListener('keydown', this.handleFirstTab);
    this.windowWidthCheck();
    window.addEventListener('resize', () => {
      this.windowWidthCheck();
      // this.setBodyHeight();
    });
    this.setIsKHUserToOnboarding();
    this.setLearnedLanguage();
    this.setUserLanguage();
    this.$eventBus.onNotificationTriggered(this, (payload) => {
      this.notification = payload;
      if (payload.timeout) {
        this.notificationTimeout(payload.timeout);
      }
    });
    this.$eventBus.onNotificationExpired(this, () => {
      this.notification = null;
    });
    this.$eventBus.onDocumentTitleChanged(this, (payload) => {
      this.lastTitleValue = payload;
      if (document.visibilityState == 'hidden') {
        document.title = this.$t('app.hiddenTabTitle');
      } else {
        document.title = this.lastTitleValue;
      }
    });
    this.$eventBus.onShowModal(this, (payload) => {
      if (this.modelOpened && this.modalLock) {
        return;
      }
      if (
        ![
          'PriceCommunicationModal',
          'ConfirmationModal',
          'ThematicNotSolved',
          'GenericModal',
        ].includes(payload?.bind.is.name)
      ) {
        this.lastModalComponent = payload;
      }
      this.modelOpened = true;
      this.modalComponent = payload;
      this.modalLock = payload.bind && payload.bind.lock == true;
      if (!this.modalComponent.noScrollChange) {
        this.afterPopperEnter();
      }
    });
    this.$eventBus.onCloseModal(this, () => {
      this.isBlur = !this.isBlur;
      this.modelOpened = false;
      this.modalLock = false;
      if (this.modalComponent && !this.modalComponent.noScrollChange) {
        this.beforePopperLeave();
      }
      if (this.modalComponent) {
        if (
          this.modalComponent.bind.is.name === 'PriceCommunicationModal' ||
          (this.modalComponent.bind.is.name === 'GenericModal' &&
            this.modalComponent.bind.is.props.needPreviousModal)
        ) {
          this.modalComponent = this.lastModalComponent;
        } else {
          this.modalComponent = null;
          this.lastModalComponent = null;
        }
      }
    });
    this.$eventBus.onShowPopper(this, (payload) => {
      this.popperComponent = payload;
    });
    this.$eventBus.onClosePopper(this, () => {
      this.popperComponent = null;
    });
    this.$eventBus.onStartButtonModal(this, (payload) => {
      this.modalComponent = payload;
    });
    this.$eventBus.onShowSuggester(this, (payload) => {
      this.showSuggester = payload;
    });
    document.addEventListener('keyup', this.handleEscape);
  },
  mounted() {
    this.setBodyHeight();
    this.handleKHRelogin();
  },
  beforeDestroy() {
    document.removeEventListener('keyup', this.handleEscape);
    document.removeEventListener('visibilitychange', this.handleFocus);
  },
  computed: {
    ...mapGetters({
      getUserOverwriteBrowserLanguage: 'getUserOverwriteBrowserLanguage',
      getUserOverwriteBrowserLanguagePersisted:
        'settings/getUserOverwriteBrowserLanguagePersisted',
      getSiteLanguage: 'settings/getSiteLanguage',
      getSelectedLearnedLanguage: 'settings/getSelectedLearnedLanguage',
      getWCAGSettings: 'settings/getWCAGSettings',
      loggedInUserProfile: 'users/loggedInUserProfile',
      hasToken: 'hasToken',
    }),
    needToHideMainLayout() {
      return (
        ['Lesson', 'SettingsScreen'].includes(this.$route.name) ||
        this.popperComponent !== null
      );
    },
    currentRoute() {
      return this.$route.path;
    },
    isLessonWithoutUser() {
      return location.href.includes('x-key');
    },
    needBlurBackground() {
      return !!(
        this.showSuggester ||
        this.modalComponent ||
        this.isBlockedNotification
      );
    },
    isBlockedNotification() {
      return !!this.notification && this.notification.isBlocked;
    },
    modalKey() {
      if (
        !this.modalComponent ||
        !this.modalComponent.bind ||
        !this.modalComponent.bind.is ||
        !this.modalComponent.bind.is.name
      ) {
        return Math.floor(Math.random() * Math.floor(100000));
      }
      return `${this.modalComponent.bind.is.name}_${Math.floor(
        Math.random() * Math.floor(1000),
      )}`;
    },
    mainLayoutIsVisible() {
      return (
        !this.$route.path.split('/')[1].includes('dkt') &&
        !location.pathname.split('/')[1].includes('dkt')
      );
    },
  },
  methods: {
    ...mapActions({
      setWindowWidthMobile: 'settings/setWindowWidthMobile',
      setWindowWidth: 'settings/setWindowWidth',
      setSelectedLearnedLanguage: 'settings/setSelectedLearnedLanguage',
      setUserOverwriteBrowserLanguagePersisted:
        'settings/setUserOverwriteBrowserLanguagePersisted',
      setIsKHUser: 'settings/setIsKHUser',
      userLogout: 'users/logout',
      logoutBackend: 'users/logoutBackend',
    }),
    handleFocus() {
      if (
        cookieHandler.getCookie('go-to-mobile-clicked') === true ||
        cookieHandler.getCookie('go-to-mobile-clicked') === 'true'
      ) {
        document.cookie =
          'go-to-mobile-clicked=;expires=Thu, 01 Jan 1970 00:00:00 GMT; secure; HostOnly; domain=.xeropan.com; path=/';
        this.logoutBackend();
        window.$cookies.set('dkt-logged-out', true, null, null, '.xeropan.com');
        this.userLogout(true).then(() => {
          setTimeout(() => {
            location.href = '/';
          }, 250);
        });
      }
    },
    handleKHRelogin() {
      if (
        location.href.includes('authentication') &&
        this.hasToken &&
        !this.loggedInUserProfile.user.isKozigMember
      ) {
        this.showGenericModal(
          {
            title: this.$t('app.khReloginModal.title'),
            description: this.$t('app.khReloginModal.paragraph'),
          },
          {
            buttonWidht: 264,
            okButtonFontSize: '16px',
            okText: this.$t('app.khReloginModal.acceptButtonText'),
            cancelText: this.$t('app.khReloginModal.closeButtonText'),
            okAction: () => {
              this.logoutBackend();
              PushNotificationManager.unsubscribeUser(() => { });
              window.$cookies.set(
                'session_timeout',
                false,
                null,
                null,
                '.xeropan.com',
              );
              this.userLogout(true).then(() => {
                this.$eventBus.closeModal();
              });
            },
            cancelAction: () => {
              if (this.$route.name === 'OnboardingScreen') {
                let newLocation = location.href.replace('kozigazgatas.', '');
                newLocation = newLocation.substring(
                  0,
                  newLocation.indexOf('/o'),
                );
                location = newLocation;
              } else {
                this.$eventBus.closeModal();
              }
            },
            escAction: () => {
              if (this.$route.name === 'OnboardingScreen') {
                let newLocation = location.href.replace('kozigazgatas.', '');
                newLocation = newLocation.substring(
                  0,
                  newLocation.indexOf('/o'),
                );
                location = newLocation;
              } else {
                this.$eventBus.closeModal();
              }
            },
          },
        );
      }
    },
    handleEscape(event) {
      if (event.key == 'Escape') {
        if (
          this.modalComponent &&
          this.modalComponent.bind.is.name === 'RecaptchaModal'
        ) {
          return;
        }
        this.$eventBus.closeModalOrPopper({
          isModalOn: this.modalComponent !== null,
        });

        if (
          this.popperComponent &&
          this.popperComponent.bind.is.name === 'LanguagesSelectorPopper' &&
          !this.$route.path.includes('settings')
        ) {
          this.$eventBus.closeLanguageSelectorIsland();
        }
      }
    },
    setBodyHeight() {
      // document.getElementById('app').style.height = `${window.innerHeight}px`;
      document.getElementById('app').style.height = '100%';
    },
    handleFirstTab(e) {
      if (e.keyCode === 9) {
        document.body.classList.add('user-is-tabbing');
        window.removeEventListener('keydown', this.handleFirstTab);
        window.addEventListener('mousedown', this.handleMouseDownOnce);
      }
    },
    handleMouseDownOnce() {
      document.body.classList.remove('user-is-tabbing');
      window.removeEventListener('mousedown', this.handleMouseDownOnce);
      window.addEventListener('keydown', this.handleFirstTab);
    },
    windowWidthCheck() {
      this.setWindowWidth(window.innerWidth);
      if (window.innerWidth <= 768) {
        this.setWindowWidthMobile(true);
      } else {
        this.setWindowWidthMobile(false);
      }
    },
    setLearnedLanguage() {
      const learnedLanguage =
        cookieHandler.getCookie('learned-language') || 'en';
      if (
        !this.getSelectedLearnedLanguage ||
        this.getSelectedLearnedLanguage != learnedLanguage
      ) {
        if (learnedLanguage == 'kh') {
          this.setSelectedLearnedLanguage('enpam');
        } else {
          this.setSelectedLearnedLanguage(learnedLanguage);
        }
      }
    },
    setIsKHUserToOnboarding() {
      const isKHUser =
        location.search.includes('authentication') ||
        this.loggedInUserProfile.user.isKozigMember ||
        false;
      this.setIsKHUser(isKHUser);
    },
    setUserLanguage() {
      if (this.isLessonWithoutUser) {
        const helperLang = this.$route.query.lang;
        // const lang =
        // helperLang?.length == 2 ? fromShortToLong[helperLang] : helperLang;
        this.$store.commit('settings/SET_USER_SELECTED_LANGUAGE', helperLang, {
          root: true,
        });
      } else if (this.$route.query.locale || this.$route.query.lang) {
        this.$store.commit(
          'settings/SET_USER_SELECTED_LANGUAGE',
          fromShortToLong[this.$route.query.locale],
          { root: true },
        );
      } else if (!this.hasToken && !location.href.includes('authentication')) {
        if (!location.pathname.includes('onboarding')) {
          this.setUserOverwriteBrowserLanguagePersisted(false);
        }
        const full = window.location.host;
        const parts = full.split('.');
        const sub = parts[0];
        if (
          sub &&
          (sub.length == 2 || sub.length == 3) &&
          !this.getUserOverwriteBrowserLanguagePersisted &&
          !this.getUserOverwriteBrowserLanguage
        ) {
          i18n.locale = fromShortToLong[sub];
          const html = document.documentElement;
          const englishSites = ['zh', 'uz', 'ky'];
          html.setAttribute('lang', englishSites.includes(sub) ? 'en' : sub);
        } else {
          const findLanguage = this.getSiteLanguage;
          i18n.locale = findLanguage;
          const html = document.documentElement;
          const englishSites = ['zh', 'uz', 'ky'];
          const foundLanguage = findLanguage.split('_')[0];
          html.setAttribute('lang', englishSites.includes(foundLanguage) ? 'en' : foundLanguage);
        }
      } else {
        this.$store.commit(
          'settings/SET_USER_SELECTED_LANGUAGE',
          this.getSiteLanguage,
          { root: true },
        );
      }
    },
    notificationTimeout(timeout) {
      this.notificationTimeoutId = setTimeout(() => {
        this.deleteNotification();
      }, timeout);
    },
    deleteNotification() {
      this.$eventBus.$emit('NotificationExpired');
    },
    afterPopperEnter() {
      document.body.classList.add('no-scroll');
    },
    beforePopperLeave() {
      document.body.classList.remove('no-scroll');
    },
  },
  watch: {
    currentRoute(newValue) {
      if (
        document.body.classList.contains('no-scroll') &&
        this.$route.name !== 'Island'
      ) {
        document.body.classList.remove('no-scroll');
        const routerContainer = document.getElementById('router-container');
        if (routerContainer?.style) {
          routerContainer.style.cssText = 'overflow-y: auto';
        }
      }
      setTimeout(() => {
        if (newValue == '/') {
          document.title = this.$t('app.title');
        } else if (newValue == '/skills') {
          document.title = this.$t('app.routes.library.name');
        } else if (newValue == '/skills/learn-words') {
          document.title = this.$t('app.routes.wordBuilder.name');
        } else if (newValue == '/skills/learn-words/favorite-words') {
          document.title = this.$t(
            'app.routes.wordBuilderFavoriteWordsScreen.name',
          );
        } else if (newValue == '/skills/speaking-practice') {
          document.title = this.$t('app.routes.teachBot.name');
        } else if (newValue == '/skills/grammar-guide') {
          document.title = this.$t('app.routes.grammar.name');
        } else if (newValue == '/skills/catalog') {
          document.title = this.$t('app.routes.catalog.name');
        } else if (newValue.includes('/profile')) {
          document.title = this.$t('app.tabs.profile');
        } else if (newValue == '/pro') {
          document.title = this.$t('app.routes.shop.name');
        } else if (newValue == '/settings') {
          document.title = this.$t('app.routes.settings.name');
        } else if (newValue.includes('topics')) {
          document.title = this.$t('app.tabs.learn');
        } else if (newValue.includes('/classes/')) {
          document.title = this.$t('app.islands.myClasses.title');
        }
      }, 500);
    },
  },
};
</script>
